import type { Container } from 'konva/lib/Container'
import type { Shape } from 'konva/lib/Shape'
import type { Arc } from 'konva/lib/shapes/Arc'
import type { Line } from 'konva/lib/shapes/Line'
import type { RegularPolygon } from 'konva/lib/shapes/RegularPolygon'
import { SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { getShapeById } from '~/composables/shapes/utils'
import type { Line as LineType, ShapeClientRect, ShapeInterface, ShapeType, Vector2d } from '~/types/grid.types'

export const resolveBlankCanvasClickTool = ({
  returnToAddOnBlankClick,
  selectedTool,
  selectedCount,
}: {
  returnToAddOnBlankClick: boolean
  selectedTool: SELECTABLE_TOOLS | null
  selectedCount: number
}) => {
  // Don't change tool when lasso or rect-select is active
  if (selectedTool === SELECTABLE_TOOLS.LASSO || selectedTool === SELECTABLE_TOOLS.RECT_SELECT) return selectedTool
  return returnToAddOnBlankClick
    ? SELECTABLE_TOOLS.SELECT
    : selectedTool
}

export const getShapeComponent = (type: ShapeType) => {
  switch (type) {
    case 'rect':
      return 'v-rect'
    case 'rect-triangle':
    case 'diamond':
    case 'triangle':
      return 'v-line'
    case 'arc':
      return 'v-arc'
    case 'hex':
      return 'v-regular-polygon'
    default:
      throw new Error(`Invalid shape type ${type}`)
  }
}

export const haveIntersection = (r1: ShapeClientRect, r2: ShapeClientRect) => {
  const tolerance = 1
  return !(
    r2.x >= r1.x + r1.width - tolerance
    || r2.x + r2.width <= r1.x + tolerance
    || r2.y >= r1.y + r1.height - tolerance
    || r2.y + r2.height <= r1.y + tolerance
  )
}

export const checkRotationCollision = (
  targetItem: { id: string; x: number; y: number; width?: number; height?: number; rotation?: number; type?: string; metadata?: any; attrs?: any },
  newShapeId: string,
  newRotation: number,
  shapeList: any[],
  gridPatternId: string = '3d',
): boolean => {
  // Only enforce grid boundary collisions on axis-aligned grids (Square and 3D)
  if (gridPatternId && gridPatternId !== 'square' && gridPatternId !== '3d') {
    return false
  }

  const newShapeDef = getShapeById(newShapeId)
  const targetW = newShapeDef?.w ?? targetItem.width ?? targetItem.attrs?.width ?? 120
  const targetH = newShapeDef?.h ?? targetItem.height ?? targetItem.attrs?.height ?? 120
  const isTargetTurned = Math.abs((newRotation || 0) % 180) === 90
  const targetWidth = isTargetTurned ? targetH : targetW
  const targetHeight = isTargetTurned ? targetW : targetH

  const targetX = targetItem.x ?? targetItem.attrs?.x ?? 0
  const targetY = targetItem.y ?? targetItem.attrs?.y ?? 0

  const targetBox: ShapeClientRect = {
    x: targetX - targetWidth / 2,
    y: targetY - targetHeight / 2,
    width: targetWidth,
    height: targetHeight,
  }

  for (const other of shapeList) {
    if (other.id === targetItem.id) continue

    const otherShapeId = other.metadata?.shape_id || other.attrs?.metadata?.shape_id
    const otherDef = otherShapeId ? getShapeById(otherShapeId) : null
    const otherW = otherDef?.w ?? other.width ?? other.attrs?.width ?? 120
    const otherH = otherDef?.h ?? other.height ?? other.attrs?.height ?? 120
    const otherRotation = other.rotation ?? other.attrs?.rotation ?? 0
    const isOtherTurned = Math.abs(otherRotation % 180) === 90
    const otherWidth = isOtherTurned ? otherH : otherW
    const otherHeight = isOtherTurned ? otherW : otherH

    const otherX = other.x ?? other.attrs?.x ?? 0
    const otherY = other.y ?? other.attrs?.y ?? 0

    const otherBox: ShapeClientRect = {
      x: otherX - otherWidth / 2,
      y: otherY - otherHeight / 2,
      width: otherWidth,
      height: otherHeight,
    }

    if (haveIntersection(targetBox, otherBox)) {
      return true
    }
  }

  return false
}

export const findNearbyNonCollidingPosition = (
  targetItem: { id: string; x: number; y: number; width?: number; height?: number; rotation?: number; type?: string; metadata?: any; attrs?: any },
  newShapeId: string,
  newRotation: number,
  shapeList: any[],
  gridSnap: number = 30,
  maxSteps: number = 12,
  gridPatternId: string = '3d',
): { x: number; y: number } | null => {
  const currentX = targetItem.x ?? targetItem.attrs?.x ?? 0
  const currentY = targetItem.y ?? targetItem.attrs?.y ?? 0

  // If not on an axis-aligned grid, keep current position
  if (gridPatternId && gridPatternId !== 'square' && gridPatternId !== '3d') {
    return { x: currentX, y: currentY }
  }

  // If in-place rotation does not collide, stay at current position
  if (!checkRotationCollision(targetItem, newShapeId, newRotation, shapeList, gridPatternId)) {
    return { x: currentX, y: currentY }
  }

  interface OffsetCandidate {
    dx: number
    dy: number
    distSq: number
  }

  const candidates: OffsetCandidate[] = []

  for (let step = 1; step <= maxSteps; step++) {
    const d = step * gridSnap
    candidates.push({ dx: -d, dy: 0, distSq: d * d })
    candidates.push({ dx: d, dy: 0, distSq: d * d })
    candidates.push({ dx: 0, dy: -d, distSq: d * d })
    candidates.push({ dx: 0, dy: d, distSq: d * d })

    for (let s2 = 1; s2 <= step; s2++) {
      const d2 = s2 * gridSnap
      candidates.push({ dx: -d, dy: -d2, distSq: d * d + d2 * d2 })
      candidates.push({ dx: -d, dy: d2, distSq: d * d + d2 * d2 })
      candidates.push({ dx: d, dy: -d2, distSq: d * d + d2 * d2 })
      candidates.push({ dx: d, dy: d2, distSq: d * d + d2 * d2 })

      if (s2 !== step) {
        candidates.push({ dx: -d2, dy: -d, distSq: d2 * d2 + d * d })
        candidates.push({ dx: -d2, dy: d, distSq: d2 * d2 + d * d })
        candidates.push({ dx: d2, dy: -d, distSq: d2 * d2 + d * d })
        candidates.push({ dx: d2, dy: d, distSq: d2 * d2 + d * d })
      }
    }
  }

  candidates.sort((a, b) => a.distSq - b.distSq)

  const newShapeDef = getShapeById(newShapeId)
  const targetW = newShapeDef?.w ?? targetItem.width ?? 120
  const targetH = newShapeDef?.h ?? targetItem.height ?? 120
  const isTargetTurned = Math.abs((newRotation || 0) % 180) === 90
  const targetWidth = isTargetTurned ? targetH : targetW
  const targetHeight = isTargetTurned ? targetW : targetH

  for (const { dx, dy } of candidates) {
    const candidateX = currentX + dx
    const candidateY = currentY + dy

    // Don't jump off the top or left canvas edge
    if (candidateX - targetWidth / 2 < 20 || candidateY - targetHeight / 2 < 20) {
      continue
    }

    const testItem = { ...targetItem, x: candidateX, y: candidateY }
    if (!checkRotationCollision(testItem, newShapeId, newRotation, shapeList, gridPatternId)) {
      return { x: candidateX, y: candidateY }
    }
  }

  return null
}

export const getRotatedPosition = (layerX: number, layerY: number, offsetX: number, offsetY: number, rotation: number) => {
  const rad = (rotation || 0) * Math.PI / 180
  return {
    x: layerX - (offsetX * Math.cos(rad) - offsetY * Math.sin(rad)),
    y: layerY - (offsetX * Math.sin(rad) + offsetY * Math.cos(rad)),
  }
}

export const getDistance = (p1: Vector2d, p2: Vector2d) => {
  return Math.sqrt(
    Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2),
  )
}

export const getCenter = (p1: Vector2d, p2: Vector2d) => {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

export const getBoxCorners = (box: ShapeClientRect) => {
  return [
    { x: box.x, y: box.y }, // top-left
    { x: box.x + box.width, y: box.y }, // top-right
    { x: box.x + box.width, y: box.y + box.height }, // bottom-right
    { x: box.x, y: box.y + box.height }, // bottom-left
    { x: box.x + box.width / 2, y: box.y + box.height / 2 }, // center
  ]
}

const getRectTrianglePoints = (shape: ShapeClientRect) => {
  const { x, y, width, height } = shape;

  // default: RIGHT TOP = [0, 0, w, 0, w, h]
  const corners = [
    { x: x, y: y }, // top left
    { x: x + width, y: y }, // top right
    { x: x + width, y: y + height }, // bottom right
  ];

  const points = [];
  // Add corner points and intermediary points for each edge
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];

    // Add the corner point
    points.push(start);

    // Add 2 intermediary points along each edge
    for (let j = 1; j <= 2; j++) {
      const t = j / 5; // Divide edge into 5 segments
      points.push({
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t
      });
    }
  }

  return points
}

const getDiamondPoints = (shape: Line, scale: number, relativeTo?: Container) => {
  const rectConfig = relativeTo ? { skipStroke: true, relativeTo } : { skipStroke: true }
  const { x, y, width, height } = shape.getClientRect(rectConfig)
  const center = {
    x: x + width / 2,
    y: y + height / 2,
  }
  const pts = shape.points()
  const points = []

  for (let i = 0; i < pts.length; i+=2) {
    if(i%2===0) {
      points.push({
        x: center.x + (pts[i] * scale),
        y: center.y + (pts[i+1] * scale)
      })
    }
  }

  return points
}

const getSemiCirclePoints = (shape: Arc, relativeTo?: Container) => {
  const rectConfig = relativeTo ? { skipStroke: true, relativeTo } : { skipStroke: true }
  const cr = shape.getClientRect(rectConfig)
  const { x, y, width, height } = cr
  const center = { x: x + width / 2, y: y + height / 2 };
  const steps = 12;
  let points = [];
  const isVertical = [90, 270].includes(shape.attrs.rotation)
  const noDoubleRotation = [90, 180].includes(shape.attrs.rotation)

  function customSin(n: number, angle: number) {
    return n - (n * Math.sin(angle))
  }

  function customCos(n: number, angle: number) {
    return n / 2 + (n / 2) * Math.cos(angle)
  }

  for (let i = 0; i <= steps; i++) {
    const angle = (Math.PI * i) / steps;
    points.push({
      x: x + (isVertical ? customSin(width, angle) : customCos(width, angle)),
      y: y + (isVertical ? customCos(height, angle) : customSin(height, angle)),
    });
  }

  // Add center and midpoints along the diameter for reliable collision on small arcs
  points.push(center);
  const first = points[0];
  const last = points[steps];
  points.push({ x: (first.x + center.x) / 2, y: (first.y + center.y) / 2 });
  points.push({ x: (last.x + center.x) / 2, y: (last.y + center.y) / 2 });

  // Rotate it to default 0deg when 180, 90deg when 270.
  // Based on composable/shapes definition
  if (!noDoubleRotation) {
    points = points.map(point => rotatePoint(point, center, -180));
  }

  return points
}

const getQuarterCirclePoints = (shape: Arc, scale: number, relativeTo?: Container) => {
  const rectConfig = relativeTo ? { skipStroke: true, relativeTo } : { skipStroke: true }
  const { x, y } = shape.getClientRect(rectConfig)
  const radius = shape.outerRadius() * scale
  const steps = 6;
  const points = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const angle = t * (Math.PI / 2);
    points.push({
      x: x + radius * Math.cos(angle),
      y: y + radius * Math.sin(angle)
    });
  }

  points.push({ x, y });

  return points;
};

const getHexPoints = (shape: RegularPolygon, scale: number, relativeTo?: Container) => {
  const rectConfig = relativeTo ? { skipStroke: true, relativeTo } : { skipStroke: true }
  const cr = shape.getClientRect(rectConfig)
  const radius = shape.radius() * scale
  const center = {
    x: cr.x + cr.width / 2,
    y: cr.y + cr.height / 2
  };

  const points = Array.from({ length: 6 }).map((_, i) => {
    const angle = (Math.PI * 2 * i) / 6;
    return rotatePoint({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle)
    }, center, -90);
  });

  return points;
};

export const isPointInPolygon = (point: Vector2d, polygonPoints: number[]) => {
  let inside = false
  for (let i = 0, j = polygonPoints.length - 2; i < polygonPoints.length; i += 2) {
    const xi = polygonPoints[i]
    const yi = polygonPoints[i + 1]
    const xj = polygonPoints[j]
    const yj = polygonPoints[j + 1]

    const intersect = ((yi > point.y) !== (yj > point.y))
      && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
    j = i
  }
  return inside
}

export const boundingBoxIntersects = (
  box1: { minX: number; maxX: number; minY: number; maxY: number },
  box2: { minX: number; maxX: number; minY: number; maxY: number }
): boolean => {
  return !(
    box1.maxX < box2.minX ||
    box1.minX > box2.maxX ||
    box1.maxY < box2.minY ||
    box1.minY > box2.maxY
  )
}

export const getFrontFaceFromGroup = (group: any) => {
  if (!group || !group.id) return null
  const groupId = typeof group.id === 'function' ? group.id() : group.id
  if (!groupId || typeof groupId !== 'string' || !groupId.endsWith('-group')) return null
  const shapeId = groupId.replace('-group', '')
  return (
    group.children?.find((child: any) => {
      const childId = typeof child?.id === 'function' ? child.id() : child?.id
      return childId === shapeId
    }) || null
  )
}

export const performanceAttrs = {
  listening: false,
  perfectDrawEnabled: false,
  shadowForStrokeEnabled: false,
  hitStrokeWidth: 0,
}

// Grid edge expansion utilities
export interface GridBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export type GridEdge = 'left' | 'top' | 'right' | 'bottom';

export const calculateGridBounds = (
  gridWidth: number,
  gridHeight: number,
  startOffset: number,
  gridPos: Vector2d,
  zoom: number
): GridBounds => {
  const scaledWidth = gridWidth * zoom;
  const scaledHeight = gridHeight * zoom;

  return {
    left: gridPos.x + (startOffset * zoom),
    top: gridPos.y + (startOffset * zoom),
    right: gridPos.x + (startOffset * zoom) + scaledWidth,
    bottom: gridPos.y + (startOffset * zoom) + scaledHeight,
    width: scaledWidth,
    height: scaledHeight
  };
};

export const detectGridEdge = (
  mousePos: Vector2d,
  gridBounds: GridBounds,
  threshold: number = 10
): GridEdge[] => {
  const edges: GridEdge[] = [];

  // Check proximity to each edge
  if (Math.abs(mousePos.x - gridBounds.left) <= threshold) {
    edges.push('left');
  }
  if (Math.abs(mousePos.x - gridBounds.right) <= threshold) {
    edges.push('right');
  }
  if (Math.abs(mousePos.y - gridBounds.top) <= threshold) {
    edges.push('top');
  }
  if (Math.abs(mousePos.y - gridBounds.bottom) <= threshold) {
    edges.push('bottom');
  }

  return edges;
};

export const getResizeCursor = (edges: GridEdge[]): string | null => {
  if (edges.length === 0) return null;

  // Handle corner cases first (two edges)
  if (edges.includes('top') && edges.includes('left')) return 'nw-resize';
  if (edges.includes('top') && edges.includes('right')) return 'ne-resize';
  if (edges.includes('bottom') && edges.includes('left')) return 'sw-resize';
  if (edges.includes('bottom') && edges.includes('right')) return 'se-resize';

  // Handle single edges
  if (edges.includes('top') || edges.includes('bottom')) return 'ns-resize';
  if (edges.includes('left') || edges.includes('right')) return 'ew-resize';

  return null;
};

export interface ShapePoints {
  points: Vector2d[];
  type: ShapeType;
}

// Helper function to rotate a point around a center point
const rotatePoint = (point: Vector2d, center: Vector2d, rotation: number): Vector2d => {
  const rad = (rotation || 0) * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: Math.round(center.x + (dx * cos - dy * sin)),
    y: Math.round(center.y + (dx * sin + dy * cos)),
  };
};

export const getShapePoints = (shape: Shape, scale: number, relativeTo?: Container): ShapePoints => {
  const rectConfig = relativeTo ? { skipStroke: true, relativeTo } : { skipStroke: true }
  const cr = shape.getClientRect(rectConfig)
  const { x, y, width, height } = cr
  const { type, rotation } = shape.attrs
  const center = { x: x + width / 2, y: y + height / 2 }
  let noRotate = false
  let points: Vector2d[]

  switch (type) {
    case 'rect': {
      // adjust for shiplap since it uses rotation to get right background
      if (width !== height && rotation !== 0) {
        noRotate = true;
        center.x = x;
        center.y = y;
      }
      points = getBoxCorners(cr).slice(0, 4); // Exclude center point
      break;
    }

    case 'rect-triangle': {
      points = getRectTrianglePoints(cr)
      break;
    }

    case 'triangle': {
      const triCorners = [
        { x: x + width / 2, y: y },
        { x: x + width, y: y + height },
        { x: x, y: y + height },
      ];
      points = [];
      for (let i = 0; i < triCorners.length; i++) {
        const start = triCorners[i];
        const end = triCorners[(i + 1) % triCorners.length];
        points.push(start);
        for (let j = 1; j <= 2; j++) {
          const t = j / 5;
          points.push({
            x: start.x + (end.x - start.x) * t,
            y: start.y + (end.y - start.y) * t,
          });
        }
      }
      break;
    }

    case 'diamond': {
      points = getDiamondPoints(shape as Line, scale, relativeTo)
      break;
    }

    case 'arc': {
      if (shape.attrs.angle === 180) {
        noRotate = true
        points = getSemiCirclePoints(shape as Arc, relativeTo)
      } else {
        points = getQuarterCirclePoints(shape as Arc, scale, relativeTo)
      }
      break;
    }

    case 'hex': {
      points = getHexPoints(shape as RegularPolygon, scale, relativeTo)
      break;
    }

    default:
      throw new Error(`Unsupported shape type: ${type}`);
  }

  if (rotation !== 0 && !noRotate) {
    points = points.map(point => rotatePoint(point, center, rotation));
  }

  return { type, points };
};

export const checkShapeIntersection = (shape1: ShapePoints, shape2: ShapePoints): boolean => {
  // For rectangles, use the original bounding box check as it's more efficient
  if (shape1.type === 'rect' && shape2.type === 'rect') {
    return haveIntersection(
      { x: shape1.points[0].x, y: shape1.points[0].y, width: shape1.points[1].x - shape1.points[0].x, height: shape1.points[3].y - shape1.points[0].y },
      { x: shape2.points[0].x, y: shape2.points[0].y, width: shape2.points[1].x - shape2.points[0].x, height: shape2.points[3].y - shape2.points[0].y }
    );
  }

  // For other shapes, check if any point of one shape is inside the other
  const tolerance = 1;
  const points1 = shape1.points;
  const points2 = shape2.points;

  // Convert points to the format expected by isPointInPolygon
  const flatPoints1 = points1.flatMap(p => [p.x, p.y]);
  const flatPoints2 = points2.flatMap(p => [p.x, p.y]);

  // Check if any point of shape1 is inside shape2
  for (const point of points1) {
    if (isPointInPolygon(point, flatPoints2)) {
      // Only consider it a collision if the point is more than tolerance pixels inside
      let distanceToEdge = Infinity;
      for (let i = 0; i < points2.length; i++) {
        const p1 = points2[i];
        const p2 = points2[(i + 1) % points2.length];
        const dist = distanceToLineSegment(point, p1, p2);
        distanceToEdge = Math.min(distanceToEdge, dist);
      }
      if (distanceToEdge > tolerance) {
        return true;
      }
    }
  }

  // Check if any point of shape2 is inside shape1
  for (const point of points2) {
    if (isPointInPolygon(point, flatPoints1)) {
      let distanceToEdge = Infinity;
      for (let i = 0; i < points1.length; i++) {
        const p1 = points1[i];
        const p2 = points1[(i + 1) % points1.length];
        const dist = distanceToLineSegment(point, p1, p2);
        distanceToEdge = Math.min(distanceToEdge, dist);
      }
      if (distanceToEdge > tolerance) {
        return true;
      }
    }
  }

  return false;
};

// Helper function to calculate distance from point to line segment
const distanceToLineSegment = (point: Vector2d, lineStart: Vector2d, lineEnd: Vector2d): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;

  return Math.sqrt(dx * dx + dy * dy);
};

// Mobile viewport utilities
const FOOT_IN_PIXELS = 120;

/**
 * Calculate optimal zoom level for mobile devices to ensure a target area is visible
 * @param gridWidth - Grid width in pixels
 * @param gridHeight - Grid height in pixels
 * @param viewportWidth - Available viewport width in pixels
 * @param viewportHeight - Available viewport height in pixels
 * @param targetFeet - Target visible area in feet (default: 7ft × 7ft)
 * @returns Optimal zoom level between 0.2 and 1.0
 */
export const calculateMobileZoom = (
  gridWidth: number,
  gridHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  targetFeet: number = 7
): number => {
  // Calculate target size in pixels
  const targetSize = targetFeet * FOOT_IN_PIXELS; // 7ft = 840px

  // Account for padding/margins (40px on each side = 80px total)
  const padding = 80;
  const availableWidth = viewportWidth - padding;
  const availableHeight = viewportHeight - padding;

  // Calculate zoom needed to fit target area in viewport
  const zoomToFitWidth = availableWidth / targetSize;
  const zoomToFitHeight = availableHeight / targetSize;

  // Use the smaller zoom to ensure both dimensions fit
  let calculatedZoom = Math.min(zoomToFitWidth, zoomToFitHeight);

  // Apply constraints:
  // - Minimum zoom: 0.2 (don't zoom out too much, readability threshold)
  // - Maximum zoom: 1.0 (don't zoom in on mobile)
  calculatedZoom = Math.max(0.2, Math.min(1.0, calculatedZoom));

  return calculatedZoom;
};

/**
 * Calculate centered position for the grid in the viewport
 * @param gridWidth - Grid width in pixels
 * @param gridHeight - Grid height in pixels
 * @param viewportWidth - Viewport width in pixels
 * @param viewportHeight - Viewport height in pixels
 * @param zoom - Current zoom level
 * @param startOffset - Grid start offset (default: 20)
 * @returns Position vector for centering the grid
 */
export const calculateMobileCenterPosition = (
  gridWidth: number,
  gridHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  zoom: number,
  startOffset: number
): Vector2d => {
  // Calculate scaled grid dimensions
  const scaledGridWidth = gridWidth * zoom;
  const scaledGridHeight = gridHeight * zoom;

  // Account for startOffset (default 20px)
  const scaledOffset = startOffset * zoom;

  // Center the grid in the viewport
  const x = (viewportWidth - scaledGridWidth) / 2 - scaledOffset;
  const y = (viewportHeight - scaledGridHeight) / 2 - scaledOffset;

  return { x, y };
};

export const getTopFacePosition = (frontRect: ShapeClientRect) => {
  const {x, y, height} = frontRect
  return {
    x: x,
    y: y - height / 2
  }
}

export const getLeftFacePosition = (frontRect: ShapeClientRect) => {
  const {x, y, width} = frontRect
  return {
    x: x - width / 2,
    y: y
  }
}

/**
 * Creates a perspective Line shape for the top face of a 3D shape
 * The top edge is skewed to the right by depth pixels to create perspective
 */
const createTopFaceLine = (image: HTMLImageElement | null, depth: number, rotation: number, frontRect: ShapeClientRect) => {
  const {width, height} = frontRect

  // Create parallelogram points for perspective
  // The top edge is shifted right by depth to create perspective effect
  // Points are relative to the shape's center (offsetX, offsetY)
  const w = width
  const h = depth
  const offsetX = 0
  const offsetY = -depth/2
  const {x, y} = getTopFacePosition(frontRect)

  // Points in local coordinates (centered at origin)
  // Top-left, Top-right (shifted right by depth), Bottom-right, Bottom-left
  const points = [
    -w / 2, -h / 2,           // top-left
    w / 2, -h / 2,            // top-right (shifted right by depth for perspective)
    w / 2 + depth, h / 2,     // bottom-right (connects to front face) scaled by 2 for perspective
    -w / 2 + depth, h / 2     // bottom-left (connects to front face)
  ]

  const newLine: Partial<LineType> & { fill?: string, opacity?: number } = {
    id: 'temp-id',
    type: 'rect-triangle', // Using rect-triangle type for closed polygon
    x: x,
    y: y,
    width: w,
    height: h,
    tension: 0,
    closed: true,
    rotation: rotation,
    points: points,
    offsetX: offsetX,
    offsetY: offsetY,
  }

  if (!image) {
    newLine.fill = '#000000'
    newLine.opacity = 0.3

    return newLine
  }

  image.crossOrigin = 'Anonymous'
  newLine.fillPatternImage = image
  newLine.fillPatternOffset = { x: w / 2, y: h / 2 }
  newLine.fillPatternRotation = 0
  newLine.fillPatternRepeat = 'repeat'
  newLine.fillPatternScale = { x: 2, y: 1 }
  newLine.opacity = 1

  image.onerror = (e) => {
    console.warn('An error occurred to load the image', e)
  }

  return newLine
}

/**
 * Creates a perspective Line shape for the left face of a 3D shape
 * The left edge is skewed down by depth pixels to create perspective
 */
const createLeftFaceLine = (image: HTMLImageElement | null, depth: number, rotation: number, frontRect: ShapeClientRect) => {
  const {width, height} = frontRect

  // Create parallelogram points for perspective
  // The left edge is shifted down by depth to create perspective effect
  // Points are relative to the shape's center (offsetX, offsetY)
  const w = depth
  const h = height
  const offsetX = -depth/2
  const offsetY = 0
  const {x, y} = getLeftFacePosition(frontRect)

  // Points in local coordinates (centered at origin)
  // Top-left (shifted down by depth), Top-right, Bottom-right, Bottom-left
  const points = [
    -w / 2, -h / 2,             // top-left (shifted down by depth for perspective)
    w / 2, -h / 2 + depth,      // top-right (connects to front face)
    w / 2, h / 2 + depth,       // bottom-right (connects to front face)
    -w / 2, h / 2               // bottom-left
  ]

  const newLine: Partial<LineType> & { fill?: string, opacity?: number } = {
    id: 'temp-id',
    type: 'rect-triangle', // Using rect-triangle type for closed polygon
    x: x,
    y: y,
    width: w,
    height: h, // Account for the perspective skew in height
    tension: 0,
    closed: true,
    rotation: rotation,
    points: points,
    offsetX: offsetX,
    offsetY: offsetY,
  }

  if (!image) {
    newLine.fill = '#000000'
    newLine.opacity = 0.6
    return newLine
  }

  image.crossOrigin = 'Anonymous'
  newLine.fillPatternImage = image
  newLine.fillPatternOffset = { x: w / 2, y: h / 2 }
  newLine.fillPatternRotation = 0
  newLine.fillPatternRepeat = 'repeat'
  newLine.fillPatternScale = { x: 1, y: 2 }
  newLine.opacity = 1

  image.onerror = (e) => {
    console.warn('An error occurred to load the image', e)
  }

  return newLine
}

export const get3dShapeGroup = (shape: ShapeInterface) => {
  try {
    const color = shape.metadata?.photo_url || ''
    const image = shape.fillPatternImage || shape.attrs?.fillPatternImage || null
    const depth = shape.metadata?.depth || 0
    const { x, y , width, height, rotation } = shape

    // For engraved 3D tiles, use the blank color image for side faces if loaded
    const blankImage = (shape as any).blankFillPatternImage || (shape.attrs as any)?.blankFillPatternImage || null
    const sideFaceImage = (blankImage && blankImage.complete && blankImage.naturalWidth > 0) ? blankImage : image

    const frontPosition: Vector2d = {
      x: Number(shape.x) + depth,
      y: Number(shape.y) + depth
    }

    const frontFace = shape
    const isTurned = Math.abs((rotation || 0) % 180) === 90
    const effWidth = isTurned ? Number(height) : Number(width)
    const effHeight = isTurned ? Number(width) : Number(height)
    const frontRect: ShapeClientRect = {x: frontFace.x, y: frontFace.y, width: effWidth, height: effHeight}
    const topFace = createTopFaceLine(sideFaceImage, depth, 0, frontRect)
    const topFaceBlack = createTopFaceLine(null, depth, 0, frontRect)
    const leftFace = createLeftFaceLine(sideFaceImage, depth, 0, frontRect)
    const leftFaceBlack = createLeftFaceLine(null, depth, 0, frontRect)

    // Separate depth faces (rendered first) from front face (rendered last)
    const depthFaces = [
      {...leftFace, id: `${shape.id}-left`, ignoreCollision: true },
      {...topFace, id: `${shape.id}-top`, ignoreCollision: true },
      {...topFaceBlack, id: `${shape.id}-top-black`, ignoreCollision: true},
      {...leftFaceBlack, id: `${shape.id}-left-black`, ignoreCollision: true},
    ]

    const frontFaceWithPosition = {
      ...frontFace,
      ...frontPosition,
      attrs: { ...frontFace.attrs, ...frontPosition }
    }

    // Keep faces array for backwards compatibility
    const faces = [
      ...depthFaces,
      frontFaceWithPosition,
    ]

    return {
      id:  `${shape.id}-group`,
      type: 'group',
      depth,
      faces: faces,
      depthFaces: depthFaces,
      frontFace: frontFaceWithPosition,
    }
  } catch (error) {
    console.error('Unable to create 3d shape group', error)
    return []
  }
}

// ============================================================================
// 3D Z-ORDERING WITH PAIRWISE OVERLAP TESTING
// ============================================================================
// For oblique projection where depth projects to (+x, +y):
// - Upper-left on screen = closer to viewer = draw LAST
// - Lower-right on screen = further from viewer = draw FIRST
//
// Simple sorting by (x + y) doesn't work for different-sized boxes.
// We use pairwise comparison with separating axis test.

export interface Box3D {
  shapeId: string
  backLeft: number
  backRight: number
  backTop: number
  backBottom: number
  frontRight: number
  frontBottom: number
  depth: number
}

export const getBox3D = (shape: ShapeInterface, pendingUpdate?: { x: number, y: number }): Box3D => {
  const centerX = pendingUpdate?.x ?? shape.x ?? shape.attrs?.x ?? 0
  const centerY = pendingUpdate?.y ?? shape.y ?? shape.attrs?.y ?? 0
  const width = shape.width ?? shape.attrs?.width ?? 0
  const height = shape.height ?? shape.attrs?.height ?? 0
  const depth = shape.metadata?.depth ?? 0

  return {
    shapeId: shape.id,
    backLeft: centerX - width / 2,
    backRight: centerX + width / 2,
    backTop: centerY - height / 2,
    backBottom: centerY + height / 2,
    frontRight: centerX + width / 2 + depth,
    frontBottom: centerY + height / 2 + depth,
    depth
  }
}

// Check if two boxes overlap on screen (2D projection)
export const boxes2DOverlap = (a: Box3D, b: Box3D): boolean => {
  // Full 2D extent: X from backLeft to frontRight, Y from backTop to frontBottom
  if (a.frontRight <= b.backLeft || b.frontRight <= a.backLeft) return false
  if (a.frontBottom <= b.backTop || b.frontBottom <= a.backTop) return false
  return true
}

// Separating axis test - determines if A should be drawn BEFORE B (A is further from viewer)
// In oblique projection with depth projecting to (+x, +y):
// - If A's back face starts at or beyond B's back face right edge, A is further on X
// - If A's back face starts at or beyond B's back face bottom edge, A is further on Y
export const shouldDrawBefore = (a: Box3D, b: Box3D): boolean => {
  // A's back-left >= B's back-right means A is entirely to the right of B's back face
  // Being to the RIGHT = further from viewer = draw first
  if (a.backLeft >= b.backRight) return true

  // A's back-top >= B's back-bottom means A is entirely below B's back face
  // Being BELOW = further from viewer = draw first
  if (a.backTop >= b.backBottom) return true

  return false
}

// 3D z-order using topological sort with pairwise overlap testing
export const sort3dShapeList = (shapeList: ShapeInterface[], pendingUpdates?: Map<string, { x: number, y: number }>) => {
  if (shapeList.length <= 1) return [...shapeList]

  // Convert to Box3D
  const boxes = shapeList.map(s => getBox3D(s, pendingUpdates?.get(s.id)))

  // Build dependency graph: edge A→B means A should be drawn before B
  const graph = new Map<string, string[]>()
  const inDegree = new Map<string, number>()

  for (const box of boxes) {
    graph.set(box.shapeId, [])
    inDegree.set(box.shapeId, 0)
  }

  // Pairwise comparison
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i]
      const b = boxes[j]

      // Skip if boxes don't overlap on screen
      if (!boxes2DOverlap(a, b)) continue

      if (shouldDrawBefore(a, b)) {
        // A is further from viewer, draw A first
        graph.get(a.shapeId)!.push(b.shapeId)
        inDegree.set(b.shapeId, inDegree.get(b.shapeId)! + 1)
      } else if (shouldDrawBefore(b, a)) {
        // B is further from viewer, draw B first
        graph.get(b.shapeId)!.push(a.shapeId)
        inDegree.set(a.shapeId, inDegree.get(a.shapeId)! + 1)
      } else {
        // Ambiguous - use fallback: higher (backRight + backBottom) = further = draw first
        const aIso = a.backRight + a.backBottom
        const bIso = b.backRight + b.backBottom
        if (aIso > bIso) {
          graph.get(a.shapeId)!.push(b.shapeId)
          inDegree.set(b.shapeId, inDegree.get(b.shapeId)! + 1)
        } else if (bIso > aIso) {
          graph.get(b.shapeId)!.push(a.shapeId)
          inDegree.set(a.shapeId, inDegree.get(a.shapeId)! + 1)
        } else {
          // Same iso-depth: deeper protrusion = closer = draw last
          if (a.depth < b.depth) {
            graph.get(a.shapeId)!.push(b.shapeId)
            inDegree.set(b.shapeId, inDegree.get(b.shapeId)! + 1)
          } else {
            graph.get(b.shapeId)!.push(a.shapeId)
            inDegree.set(a.shapeId, inDegree.get(a.shapeId)! + 1)
          }
        }
      }
    }
  }

  // Topological sort (Kahn's algorithm)
  const result: string[] = []
  const queue: string[] = []

  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id)
  }

  while (queue.length > 0) {
    // Stable sort: process higher iso-depth first when multiple have in-degree 0
    queue.sort((aId, bId) => {
      const a = boxes.find(b => b.shapeId === aId)!
      const b = boxes.find(b => b.shapeId === bId)!
      return (b.backRight + b.backBottom) - (a.backRight + a.backBottom)
    })

    const current = queue.shift()!
    result.push(current)

    for (const neighbor of graph.get(current) || []) {
      const newDegree = inDegree.get(neighbor)! - 1
      inDegree.set(neighbor, newDegree)
      if (newDegree === 0) queue.push(neighbor)
    }
  }

  // Handle cycles (shouldn't happen, but fallback gracefully)
  if (result.length !== boxes.length) {
    console.warn('[3D Sort] Cycle detected, falling back to iso-depth sort')
    return [...shapeList].sort((a, b) => {
      const aBox = boxes.find(box => box.shapeId === a.id)!
      const bBox = boxes.find(box => box.shapeId === b.id)!
      const aIso = aBox.backRight + aBox.backBottom
      const bIso = bBox.backRight + bBox.backBottom
      if (aIso !== bIso) return bIso - aIso
      return aBox.depth - bBox.depth
    })
  }

  // Map back to shapes
  const shapeMap = new Map(shapeList.map(s => [s.id, s]))
  return result.map(id => shapeMap.get(id)!).filter(Boolean)
}

/**
 * Calculate positions for multiple shapes based on quantity
 * @param quantity - Number of shapes (1, 2, 4, or 8)
 * @param baseX - Base X position
 * @param baseY - Base Y position
 * @param shapeWidth - Width of each shape
 * @param shapeHeight - Height of each shape
 * @param margin - Margin between shapes
 * @returns Array of position objects with x and y coordinates
 */
export const calculateMultiShapePositions = (
  quantity: number,
  baseX: number,
  baseY: number,
  shapeWidth: number,
  shapeHeight: number,
  margin: number
): Array<{ x: number; y: number }> => {
  const positions: Array<{ x: number; y: number }> = []

  if (quantity === 2) {
    // 2 shapes: side by side
    positions.push({ x: baseX + shapeWidth + margin, y: baseY })
    positions.push({ x: baseX, y: baseY })
  } else if (quantity === 4) {
    // 4 shapes: all in one row side by side
    for (let col = 0; col < 4; col++) {
      positions.push({
        x: baseX + col * (shapeWidth + margin),
        y: baseY
      })
    }
  } else if (quantity === 8) {
    // 8 shapes: 4x2 grid (4 columns, 2 rows)
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 4; col++) {
        positions.push({
          x: baseX + col * (shapeWidth + margin),
          y: baseY + row * (shapeHeight + margin)
        })
      }
    }
  } else {
    // Default: single shape or unknown quantity
    positions.push({ x: baseX, y: baseY })
  }

  // Sort positions for 3D perspective effect: bottom-right first, top-left last
  // This ensures items further back (bottom-right) are rendered first,
  // and items in front (top-left) are rendered last
  return positions.sort((a, b) => {
    // First sort by Y descending (bottom to top)
    const yDiff = b.y - a.y
    if (yDiff !== 0) return yDiff
    // Then sort by X descending (right to left)
    return b.x - a.x
  })
}

/**
 * Move a set of items by the LEADER's delta, keeping their relative layout.
 *
 * A shape sold in a set (a 3D box comes as 2, 4 or 8 tiles) is laid out as
 * several temp items, each offset from the first by the set's arrangement
 * (see calculateMultiShapePositions). When the set is dropped on the grid the
 * leader is snapped to the pointer, and every other tile has to move by the
 * SAME delta so the arrangement survives.
 *
 * Assigning the leader's absolute position to every item instead collapses
 * the set onto one tile: the second tile lands exactly on the first, and the
 * user sees a single tile rather than the set they picked.
 *
 * @param items - The temp items of the set, leader first
 * @param leaderTarget - Where the leader should land
 * @returns The items with their new positions
 */
export const moveItemsByLeaderDelta = <T extends { x: number; y: number }>(
  items: T[],
  leaderTarget: { x: number; y: number },
): T[] => {
  const leader = items[0]
  if (!leader) return items
  const deltaX = leaderTarget.x - leader.x
  const deltaY = leaderTarget.y - leader.y
  return items.map(item => ({
    ...item,
    x: item.x + deltaX,
    y: item.y + deltaY,
  }))
}

/**
 * Converts a color label to its corresponding hex color value
 * @param {string} colorLabel - The color label (e.g., 'Zinc', 'Ebony')
 * @returns {string|null} - The hex color value or null if not found
 */
export const getColorFromLabel = (colorLabel: string) => {
  if (!colorLabel) return null

  const colorMap: Record<string, string> = {
    // Very light colors (need black text)
    'zinc': '#E8E8E8',
    'porcelain': '#F8F8F8',
    'ivory': '#FFFFF0',
    'cashmere': '#F5F5DC',
    'fog': '#F0F0F0',
    'latte': '#F5DEB3',

    // Light colors (need black text)
    'nickel': '#C0C0C0',
    'moon': '#D3D3D3',
    'citrine': '#E6E619',
    'mustard': '#FFDB58',
    'kiwi': '#8FBC8F',
    'mint': '#98FB98',
    'babyblue': '#87CEEB',
    'periwinkle': '#C5C5FF',
    'lilac': '#DDA0DD',
    'coral': '#FF7F50',
    'raspberry': '#E30B5C',
    'hotpink': '#FF69B4',
    'lavender': '#E6E6FA',
    'sky': '#87CEEB',
    'freshlime': '#32CD32',

    // Medium colors (need white text)
    'cast': '#363a43',
    'mineral': '#4a4e57',
    'armor': '#4B4B4D',
    'ash': '#B2BEB5',
    'timber': '#8B4513',
    'cocoa': '#652526',
    'cinnamon': '#D2691E',
    'moab': '#CD853F',
    'aloe': '#7CB342',
    'sage': '#9CAF88',
    'olive': '#808000',
    'palm': '#228B22',
    'atlantic': '#4682B4',
    'aqua': '#00FFFF',
    'cayman': '#4169E1',
    'slateblue': '#6A5ACD',
    'plum': '#DDA0DD',
    'aries': '#FF6347',
    'scarlet': '#FF2400',
    'ruby': '#E0115F',
    'wine': '#722F37',
    'emerald': '#50C878',
    'hunter': '#355E3B',
    'river': '#4682B4',
    'toffee': '#E2725B',
    'caramel': '#AF6E4D',
    'grass': '#7CB342',
    'zen': '#8FBC8F',
    'subway': '#C0C0C0',

    // Dark colors (need white text)
    'ebony': '#555D50',
    'jasper': '#2F4F4F',
    'admiral': '#000080',
    'oxford': '#002147',
    'indigo': '#4B0082',
    'wisteria': '#8B5A96',
    'crimson': '#662627'
  }

  const normalizedLabel = colorLabel.toLowerCase().trim()
  return colorMap[normalizedLabel] || null
}

export const getContrastingTextColor = (backgroundColor: string) => {
  // If no background color, default to dark text
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#333'
  }

  try {
    // Remove # if present and convert to RGB
    const hex = backgroundColor.replace('#', '')

    // Validate hex format
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
      return '#333' // fallback for invalid hex
    }

    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    // Calculate luminance using the relative luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Use luminance threshold of 0.5
    return luminance > 0.5 ? '#000' : '#FFF'
  } catch (error) {
    // Fallback to dark text if any error occurs
    return '#333'
  }
}
